import { readonly, ref } from 'vue';
import Cookies from 'js-cookie';
import { VOICE_CALL_OUTBOUND_INIT_STATUS } from 'dashboard/components-next/message/constants';

// Generic WebRTC plumbing shared by every browser-side voice call provider
// (Meta Cloud API today; SIP/WaCalls reuse this via the voice-gateway
// microservice — see docs/voice-calling/phase-0-prep.md). The browser always
// speaks plain WebRTC against the backend/gateway, never SIP or WaCalls
// directly (see voice-gateway/README.md "Decisão de arquitetura").
//
// createVoiceWebrtcSession(config) builds one isolated session — call it once
// per provider at module scope (not inside the exported hook) so state stays
// a singleton across every composable instance for that provider, mirroring
// the original useWhatsappCallSession module-level state. Two providers never
// share a session object, so activeCallId/pc from one can't leak into another.
//
// config:
//   api: { show, initiate, accept, reject, terminate, uploadRecording } —
//     same contract as WhatsappCallsAPI (see phase-0-prep.md item 3).
//   apiUrlSegment: e.g. 'whatsapp_calls' — used to build the unload-beacon
//     terminate URL (`/api/v1/accounts/:accountId/:apiUrlSegment/:callId/terminate`).
//   remoteAudioElementId: dedupes the injected <audio> tag per provider.
//   logLabel: prefix for console warnings/errors, e.g. '[WhatsApp Call]'.
//   onInitiateError(error): optional. Return a value to use as
//     initiateOutboundCall's result instead of rethrowing (e.g. WhatsApp's
//     call-permission-request 422 branch). Return undefined to rethrow.

const RECORDING_TIMESLICE_MS = 1000;
const ICE_GATHER_TIMEOUT_MS = 10000;

const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

// Outbound calls have no backend-supplied ice_servers (the call doesn't exist
// at offer time). Without STUN the browser only sends host candidates and
// browser→provider media silently drops through any non-trivial NAT.
const DEFAULT_OUTBOUND_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

const waitForIceGatheringComplete = peer =>
  new Promise(resolve => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
    peer.addEventListener('icegatheringstatechange', () => {
      if (peer.iceGatheringState === 'complete') {
        clearTimeout(timer);
        resolve();
      }
    });
  });

// devise-token-auth requires access-token / client / uid headers on every
// request — navigator.sendBeacon can't set custom headers, so we rehydrate
// the auth payload from the cw_d_session_info cookie that the dashboard sets
// at login. Used by the page-close terminate path below.
const getDeviseAuthHeaders = () => {
  try {
    const raw = Cookies.get('cw_d_session_info');
    if (!raw) return null;
    const session = JSON.parse(raw);
    return {
      'access-token': session['access-token'] || '',
      client: session.client || '',
      uid: session.uid || '',
      expiry: session.expiry || '',
      'token-type': session['token-type'] || 'Bearer',
    };
  } catch (_) {
    return null;
  }
};

export function createVoiceWebrtcSession({
  api,
  apiUrlSegment,
  remoteAudioElementId,
  logLabel,
  onInitiateError,
}) {
  let pc = null;
  let localStream = null;
  let remoteStream = null;
  let remoteAudioEl = null;
  let mediaRecorder = null;
  let recorderChunks = [];
  let audioContext = null;
  let activeCallId = null;
  // outbound_connected (the sole source of the outbound SDP answer) is
  // broadcast account-wide and can arrive before the initiate response sets
  // activeCallId in this tab. Until we know our own call id we can't tell our
  // answer from a concurrent agent's, so the cable handler buffers them here
  // keyed by call id (a single slot would let a concurrent agent's event
  // clobber ours) and initiateOutboundCall flushes the matching one by id.
  const pendingOutboundAnswers = new Map();
  // Module-scoped so multiple composable callers (header button + contact-panel
  // button) share the same lock. A per-instance ref let two parallel callers
  // both pass the guard and tear down each other's WebRTC state in cleanup().
  const isInitiatingOutbound = ref(false);
  const isInitiatingOutboundReadonly = readonly(isInitiatingOutbound);
  // Inbound calls record from the moment the agent clicks accept (their click =
  // pickup). Outbound calls must wait — the provider's connect event (which
  // lands during ringing) negotiates remote tracks well before the contact
  // actually answers, and we don't want pre-pickup audio in the recording.
  // Flipped to true by armOutboundRecorder() when the ACCEPTED status arrives.
  let recorderArmed = false;

  const ensureRemoteAudioElement = () => {
    if (remoteAudioEl) return remoteAudioEl;
    remoteAudioEl = document.createElement('audio');
    remoteAudioEl.id = remoteAudioElementId;
    remoteAudioEl.autoplay = true;
    remoteAudioEl.playsInline = true;
    remoteAudioEl.style.display = 'none';
    document.body.appendChild(remoteAudioEl);
    return remoteAudioEl;
  };

  const playRemoteStream = stream => {
    const el = ensureRemoteAudioElement();
    el.srcObject = stream;
    el.play().catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`${logLabel} remote audio play() failed:`, err);
    });
  };

  const setupRecorder = () => {
    if (!localStream || !remoteStream || mediaRecorder) return;
    // createMediaStreamSource on a stream with no audio tracks wires up to
    // nothing — the recorded mix would be silence. Wait until ontrack fires.
    if (remoteStream.getAudioTracks().length === 0) return;

    audioContext = new AudioContext({ sampleRate: 48000 });
    // AudioContext starts suspended under most autoplay policies; without
    // resume() the destination stream produces silence.
    audioContext.resume().catch(() => {});

    const destination = audioContext.createMediaStreamDestination();
    audioContext.createMediaStreamSource(localStream).connect(destination);
    audioContext.createMediaStreamSource(remoteStream).connect(destination);

    const mimeType = RECORDER_MIME_CANDIDATES.find(t =>
      MediaRecorder.isTypeSupported(t)
    );
    if (!mimeType) return;

    recorderChunks = [];
    mediaRecorder = new MediaRecorder(destination.stream, { mimeType });
    mediaRecorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) recorderChunks.push(event.data);
    };
    mediaRecorder.start(RECORDING_TIMESLICE_MS);
  };

  const cleanup = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop();
      } catch (_) {
        /* noop */
      }
    }
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().catch(() => {});
    }
    if (localStream) localStream.getTracks().forEach(t => t.stop());
    if (remoteStream) remoteStream.getTracks().forEach(t => t.stop());
    if (pc) pc.close();
    if (remoteAudioEl) remoteAudioEl.srcObject = null;

    pc = null;
    localStream = null;
    remoteStream = null;
    mediaRecorder = null;
    recorderChunks = [];
    audioContext = null;
    activeCallId = null;
    pendingOutboundAnswers.clear();
    recorderArmed = false;
  };

  const buildPeerConnection = iceServers => {
    const config = iceServers && iceServers.length ? { iceServers } : {};
    pc = new RTCPeerConnection(config);
    remoteStream = new MediaStream();
    pc.ontrack = event => {
      // Reuse the same MediaStream object — the recorder's audioContext source
      // taps it once, so reassigning would orphan the recorder.
      const tracks =
        event.streams && event.streams[0]
          ? event.streams[0].getTracks()
          : [event.track];
      tracks.forEach(track => {
        if (!remoteStream.getTracks().includes(track))
          remoteStream.addTrack(track);
      });
      playRemoteStream(remoteStream);
      // Only arm the recorder when the call is actually accepted. For outbound
      // this is the ACCEPTED status webhook; for inbound this is the agent's
      // own click (acceptIncomingCall flips recorderArmed before returning).
      if (recorderArmed) setupRecorder();
    };
    return pc;
  };

  const stopRecorderAndUpload = async callId => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      await new Promise(resolve => {
        mediaRecorder.addEventListener('stop', resolve, { once: true });
        try {
          mediaRecorder.stop();
        } catch (_) {
          resolve();
        }
      });
    }
    if (!recorderChunks.length || !callId) return;

    const blob = new Blob(recorderChunks, { type: recorderChunks[0].type });
    // Best-effort — the controller's idempotency guard handles a retry.
    try {
      await api.uploadRecording(callId, blob);
    } catch (_) {
      /* noop */
    }
  };

  const beaconTerminate = callId => {
    if (!callId) return;
    const accountId = window.location.pathname.split('/')[3];
    if (!accountId) return;
    const headers = getDeviseAuthHeaders();
    if (!headers) return;
    const url = `/api/v1/accounts/${accountId}/${apiUrlSegment}/${callId}/terminate`;
    // fetch+keepalive (instead of navigator.sendBeacon) so we can attach auth
    // headers — without them devise-token-auth 401s and the call stays open on
    // the provider until its carrier-side timeout.
    try {
      fetch(url, {
        method: 'POST',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: '{}',
      }).catch(() => {});
    } catch (_) {
      /* noop */
    }
  };

  const hasActiveCall = () => !!(activeCallId || pc);

  const isLocalCall = callId =>
    !!callId && activeCallId != null && callId === activeCallId;

  const prepareInboundAnswer = async (sdpOffer, iceServers) => {
    cleanup();
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection(iceServers);
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  const prepareOutboundOffer = async () => {
    cleanup();
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection(DEFAULT_OUTBOUND_ICE_SERVERS);
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  const acceptIncomingCall = async ({ callId, sdpOffer, iceServers }) => {
    // The store may not have sdpOffer yet (the cable broadcast can race the
    // click). Fall back to GET .../:id which exposes it.
    let offer = sdpOffer;
    let ice = iceServers;
    if (!offer && callId) {
      try {
        const fresh = await api.show(callId);
        offer = fresh?.sdp_offer || fresh?.sdpOffer;
        ice = ice || fresh?.ice_servers || fresh?.iceServers;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`${logLabel} failed to fetch call data for accept:`, e);
      }
    }
    if (!offer) {
      throw new Error('Missing sdp_offer for accept — call may have ended.');
    }

    // Release the mic + peer connection if anything between here and the accept
    // round-trip fails — otherwise a rejected accept leaves the mic live and
    // activeCallId set. Mirrors rejectIncomingCall's self-cleanup; rethrow so
    // the caller can surface the failure and skip marking the call active.
    try {
      const sdpAnswer = await prepareInboundAnswer(offer, ice);
      activeCallId = callId;
      // Inbound: agent's click is the pickup. Arm the recorder before the API
      // round-trip so when ontrack fires (triggered by setRemoteDescription
      // back in prepareInboundAnswer) the recorder is already authorized.
      recorderArmed = true;
      setupRecorder();
      await api.accept(callId, sdpAnswer);
    } catch (e) {
      cleanup();
      throw e;
    }
  };

  const rejectIncomingCall = async callId => {
    try {
      await api.reject(callId);
    } finally {
      cleanup();
    }
  };

  // target: provider-specific shape forwarded as-is to api.initiate (e.g.
  // { conversationId } or { contactId, inboxId } for WhatsApp).
  const initiateOutboundCall = async target => {
    // Module-scoped lock + active-session guard so a second click — from the
    // same composable instance OR a different one (header vs contact panel)
    // OR while a call is already live — can't tear down the in-flight setup
    // via prepareOutboundOffer's cleanup().
    if (isInitiatingOutbound.value)
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    if (hasActiveCall())
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    isInitiatingOutbound.value = true;
    try {
      const sdpOffer = await prepareOutboundOffer();
      const response = await api.initiate(target, sdpOffer);
      if (response?.id) {
        activeCallId = response.id;
        // A connect event that raced ahead of this response was buffered;
        // apply our own by id now that we know it, then drop every buffered
        // answer (concurrent agents' calls aren't ours to apply).
        const buffered = pendingOutboundAnswers.get(activeCallId);
        pendingOutboundAnswers.clear();
        if (buffered) {
          await pc.setRemoteDescription({
            type: 'answer',
            sdp: buffered,
          });
        }
        return response;
      }
      // No call id back: the offer we prepared isn't useful until whatever
      // out-of-band step the provider requires happens and the agent retries
      // — release it.
      cleanup();
      return response;
    } catch (e) {
      cleanup();
      // Let the provider wrapper map known error shapes (e.g. WhatsApp's
      // call-permission-request 422) to a normal response instead of a throw.
      const mapped = onInitiateError?.(e);
      if (mapped !== undefined) return mapped;
      throw e;
    } finally {
      isInitiatingOutbound.value = false;
    }
  };

  // callIdOverride is the call.id from the dashboard's calls store. The
  // module's activeCallId may be null after a prior accept attempt's
  // cleanup() — but the call still exists on the provider and must still be
  // terminated. Falling back to the override means hangup is robust to a
  // wiped local session.
  const endActiveCall = async (callIdOverride = null) => {
    const callId = activeCallId || callIdOverride;
    if (!callId) {
      cleanup();
      return;
    }
    try {
      // Terminate on the provider first so the contact is disconnected
      // immediately. The recording upload below can be slow on long calls or
      // poor networks, and the peer connection / mic must not stay live for
      // the contact during it.
      await api.terminate(callId).catch(() => {});
      await stopRecorderAndUpload(callId);
    } finally {
      cleanup();
    }
  };

  const applyRemoteAnswer = async (callId, sdpAnswer) => {
    // No in-flight peer connection in this tab → not our call.
    if (!pc) return;
    // initiate hasn't returned our call id yet (the connect event raced
    // ahead). Buffer the answer; initiateOutboundCall flushes it by id once it
    // knows which call is ours, so a concurrent agent's answer is never applied.
    if (activeCallId == null) {
      pendingOutboundAnswers.set(callId, sdpAnswer);
      return;
    }
    // activeCallId known → only apply the answer for this tab's call.
    if (callId !== activeCallId) return;
    await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
  };

  // Called by the cable handler when the provider delivers real pickup for
  // the outbound call. Flips the recorder gate and starts the MediaRecorder.
  // Idempotent — safe if ontrack hasn't fired yet (setupRecorder bails until
  // the remote stream has audio tracks; ontrack will retry).
  const armOutboundRecorder = () => {
    recorderArmed = true;
    setupRecorder();
  };

  const handleRemoteEnd = async callId => {
    // Snapshot before cleanup nulls activeCallId.
    const id = callId || activeCallId;
    if (!id) {
      cleanup();
      return;
    }
    try {
      await stopRecorderAndUpload(id);
    } finally {
      cleanup();
    }
  };

  const setMuted = muted => {
    if (!localStream) return false;
    localStream.getAudioTracks().forEach(track => {
      track.enabled = !muted;
    });
    return muted;
  };

  // Always fire when there's a live callId. The beacon endpoint is idempotent,
  // so racing it with an in-flight Axios terminate (which unload may abort) is
  // fine — the provider gets exactly one terminate either way, and we avoid
  // leaving the call ringing until its carrier-side timeout.
  const sendTerminateBeacon = () => {
    if (!activeCallId) return;
    beaconTerminate(activeCallId);
  };

  return {
    // Vue-composable-shaped surface — a provider wrapper's useXCallSession()
    // can spread this (or a superset of it) directly.
    useSession: () => ({
      isInitiating: isInitiatingOutboundReadonly,
      prepareInboundAnswer,
      prepareOutboundOffer,
      acceptIncomingCall,
      rejectIncomingCall,
      initiateOutboundCall,
      endActiveCall,
    }),
    // Module-level functions for cable handlers / stores outside any
    // composable instance.
    hasActiveCall,
    isLocalCall,
    applyRemoteAnswer,
    armOutboundRecorder,
    cleanupSession: cleanup,
    handleRemoteEnd,
    setMuted,
    sendTerminateBeacon,
  };
}
